import classNames from 'classnames/bind';
import parse, {
  HTMLReactParserOptions,
  attributesToProps,
  domToReact,
} from 'html-react-parser';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { default as _ReactPlayer } from 'react-player';
import { ReactPlayerProps } from 'react-player/types/lib';
import { useParams } from '../../hooks/useParams';
import styles from './index.module.scss';
const ReactPlayer = _ReactPlayer as unknown as React.FC<ReactPlayerProps>;

export interface VideoProps {
  id?: string;
  autoplay?: boolean;
  url?: string;
  video?: string;
  event?: string;
  play?: boolean;
  oembed?: any;
  [key: string]: any;
}

export const Video = ({
  id,
  url,
  video,
  oembed,
  text,
  event = 'interaction',
  autoplay,
  play,
  ...rest
}: VideoProps) => {
  const cx = classNames.bind(styles);
  const elRef = useRef<HTMLElement>(null);
  const videoRef = useRef<any>(null);
  const embedRef = useRef<any>(null);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [ratio, setRatio] = useState(
    oembed ? `${oembed.width}/${oembed.height}` : '16/9',
  );
  const [overwrite, setOverwrite] = useState<any>(null);
  const { paramsString } = useParams();

  const options: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      if (
        domNode.name === 'a' &&
        domNode.attribs &&
        !domNode.attribs.href.startsWith('mailto:')
      ) {
        const props = attributesToProps(domNode.attribs);
        return (
          <a
            {...props}
            href={`/${id}/outbound?url=${encodeURIComponent(
              domNode.attribs.href,
            )}&${paramsString}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            {domToReact(domNode.children, options)}
          </a>
        );
      }
    },
  };

  const onProgress = useCallback(
    (e?: any) => {
      if (!playing) return;
      const percentage = (e?.playedSeconds ?? 0) / duration;

      const data = {
        detail: {
          type: event,
          update: true,
          data: {
            percentage,
            src: url,
          },
        },
      };

      const triggerLegacy = new CustomEvent('track', data);
      window.dispatchEvent(triggerLegacy);
    },
    [playing, url, event, duration],
  );

  const onEnded = () => {
    const trigger = new CustomEvent('track', {
      detail: {
        video: url,
        data: {
          percentage: 1,
          src: url,
        },
      },
    });
    elRef?.current?.dispatchEvent(trigger);
  };

  const getEmbed = async (uri?: string) => {
    if (!uri) return null;
    try {
      let url = `https://oembed-khaki.vercel.app/api/embed?url=${encodeURIComponent(
        uri,
      )}`;

      const res = await fetch(url).then((res) => res.json());

      return res;
    } catch (e) {
      return null;
    }
  };

  const handleDuration = (duration: number) => {
    setDuration(duration);
  };

  // const toggleMute = useCallback(() => {
  //   const player = videoRef.current;
  //   if (player) {
  //     setMuted(!muted);
  //   }
  // }, [muted]);

  useEffect(() => {
    if (oembed) return;

    const fetchData = async () => {
      const data = await getEmbed(url ?? video);
      if (data?.width && data?.height) {
        setRatio(`${data.width}/${data.height}`);
        setOverwrite(data);
      }
    };
    fetchData();
  }, [url, video, oembed]);

  const handleInteraction = (e: any) => {
    if (!embedRef.current) return;
    const iframe = embedRef.current?.querySelector('iframe');
    const active = document.activeElement;

    if (active?.isSameNode(iframe as Node) || active == iframe) {
      if (!iframe?.dataset.sent && iframe) {
        iframe.dataset.sent = 'true';

        const data = {
          detail: {
            type: 'interaction',
            data: {
              src: iframe?.src,
            },
          },
        };

        const event = new CustomEvent('track', data);
        window.dispatchEvent(event);
      }
    }
  };

  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('blur', handleInteraction);
      return () => window.removeEventListener('blur', handleInteraction);
    }
  }, []);

  if (!url) return null;

  return (
    <section
      className={cx('component', 'video', { autoplay }, rest)}
      ref={elRef}
    >
      <figure className={styles.figure} style={{ aspectRatio: ratio }}>
        <div
          className={styles.spacer}
          style={{ paddingTop: `calc(100% / (${ratio}))` }}
        />

        {!url.includes('agnoplay') && (
          <>
            {(!!url || !!video) && (
              <ReactPlayer
                url={url ?? video}
                width="100%"
                height="100%"
                playsinline={true}
                playing={autoplay ?? play}
                onStart={() => {
                  setPlaying(true);
                  onProgress();
                }}
                onProgress={onProgress}
                onEnded={onEnded}
                onDuration={handleDuration}
                progressInterval={500}
                ref={videoRef}
                controls={true}
                volume={1}
                config={{
                  youtube: {
                    playerVars: {
                      controls: 1,
                      showinfo: 0,
                      rel: 0,
                    },
                    embedOptions: {
                      host: 'https://www.youtube-nocookie.com',
                    },
                  },
                  vimeo: {
                    playerOptions: {
                      color: '#fff',
                      portrait: false,
                      byline: false,
                    },
                  },
                }}
              />
            )}
          </>
        )}

        {url.includes('agnoplay') && (oembed?.html ?? overwrite?.html) && (
          <div
            className={cx('agnoplay')}
            ref={embedRef}
            dangerouslySetInnerHTML={{
              __html: oembed?.html ?? overwrite?.html,
            }}
          />
        )}
      </figure>

      {!!text && text !== '<p><br/></p>' && (
        <figcaption className={styles.caption}>
          {parse(text, options)}
        </figcaption>
      )}
    </section>
  );
};
